/**
 * Typed selector hooks for the trades domain.
 *
 * Snapshot shape (from docs/ARCHITECTURE.md §3.4):
 *
 *   { as_of, mode,
 *     today: [ { trade_id, symbol, side, quantity, lots, entry_price,
 *                exit_price, gross_pnl, brokerage, net_pnl,
 *                entry_strategy, close_strategy, instrument_type,
 *                entry_order_id, close_order_id,
 *                opened_at, closed_at, holding_secs } ],   // newest-first
 *     summary: { count, wins, losses, gross_pnl, brokerage, net_pnl,
 *                biggest_win, biggest_loss, avg_holding_secs } }
 */
import { useStateStore } from "@/state/store";
import { EMPTY_ARRAY } from "@/state/hooks/_empty";

export interface TradeRow {
  trade_id:        number | null;
  symbol:          string;
  side:            "BUY" | "SELL";
  quantity:        number;
  lots:            number;
  entry_price:     number;
  exit_price:      number;
  gross_pnl:       number;
  brokerage:       number;
  net_pnl:         number;
  entry_strategy:  string | null;
  close_strategy:  string | null;
  entry_order_id:  string | null;
  close_order_id:  string | null;
  instrument_type: string;
  opened_at:       string | null;
  closed_at:       string | null;
  holding_secs:    number;
}

export interface TradesSummary {
  count:            number;
  wins:             number;
  losses:           number;
  gross_pnl:        number;
  brokerage:        number;
  net_pnl:          number;
  biggest_win:      number;
  biggest_loss:     number;
  avg_holding_secs: number;
}

export interface TradesSnapshot {
  as_of:   string;
  mode:    "paper" | "live";
  today:   TradeRow[];
  summary: TradesSummary;
  _seq?:   number;
}

export function useTrades(): TradesSnapshot | null {
  return useStateStore((s) => s.trades.snapshot as TradesSnapshot | null);
}

export function useTradesList(): TradeRow[] {
  return useStateStore((s) => {
    const snap = s.trades.snapshot as TradesSnapshot | null;
    return (snap?.today ?? EMPTY_ARRAY) as TradeRow[];
  });
}

export function useTradesSummary(): TradesSummary | null {
  return useStateStore((s) => {
    const snap = s.trades.snapshot as TradesSnapshot | null;
    return snap?.summary ?? null;
  });
}

export function useTradesIsStale(): boolean {
  return useStateStore((s) => s.trades.isStale);
}

export function useTradesLastUpdated(): number {
  return useStateStore((s) => s.trades.lastUpdatedAt);
}
